'use client';

import Image from 'next/image';
import Link from 'next/link';
import AdvancedProgressBar from '@/components/advancedProgressBar';
import { BookmakerStats } from '@/components/hub/BookmakerStats';
import { NavigationAnchors } from '@/components/hub/NavigationAnchors';
import { PaymentStats } from '@/components/hub/PaymentStats';
import type { Bookmaker } from '@/types/bookmaker';

type DesktopHeaderProps = {
  data: Bookmaker;
};

export const DesktopHeader = ({ data }: DesktopHeaderProps) => {
  const {
    BookmakerLogo,
    Percentage,
    PayoutTime,
    FirstDepositBonus,
    PaymentType,
    FreeSpins,
    BookmakerUrl,
  } = data.attributes;

  const imageUrl = BookmakerLogo.data.attributes.url;

  return (
    <div className="hidden w-full flex-col gap-2 rounded-lg bg-medium-gray p-2 sm:p-3 md:p-4 lg:flex">
      {/* Main content container with improved responsiveness */}
      <div className="flex flex-col gap-2 lg:flex-row lg:gap-4">
        {/* Logo container with responsive sizing */}
        <div className="h-[80px] w-full shrink-0 overflow-hidden sm:h-[90px] lg:h-[110px] lg:w-[204px]">
          <Image
            alt="bookmaker-logo-desktop-header"
            className="h-full w-full rounded-lg object-contain lg:object-cover"
            height={0}
            priority
            src={imageUrl}
            width={204}
          />
        </div>

        {/* Progress bar and stats container */}
        <div className="flex grow flex-col gap-2 lg:flex-row lg:gap-16">
          {/* Progress bar with adaptive width */}
          <div className="w-full lg:w-auto lg:flex-1">
            <AdvancedProgressBar progress={Percentage} type="medium" />
          </div>

          {/* Stats containers with flexible layout */}
          <div className="flex flex-row justify-between gap-2 lg:justify-start lg:gap-4">
            <div className="flex-1 lg:flex-none">
              <BookmakerStats
                FirstDepositBonus={FirstDepositBonus}
                isDesktop
                PayoutTime={PayoutTime}
              />
            </div>
            <div className="flex-1 lg:flex-none">
              <PaymentStats
                FreeSpins={FreeSpins}
                isDesktop
                PaymentType={PaymentType}
              />
            </div>
          </div>

          {/* CTA button container */}
          <div className="mt-2 flex items-center justify-center lg:mt-0 lg:justify-end">
            <Link
              className="h-[40px] w-full rounded-lg border border-blue-gray px-4 py-3 text-center font-semibold text-blue-gray text-sm leading-4 transition-colors duration-200 hover:bg-blue-gray hover:text-white lg:w-auto lg:max-w-[223px]"
              href={BookmakerUrl ?? ''}
              target="_blank"
            >
              Visit website
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation anchors with improved spacing */}
      <div className="mt-2">
        <NavigationAnchors data={data} />
      </div>
    </div>
  );
};
